import { useField } from 'formik';
import React, { useRef, useState } from 'react';

import styled from '@emotion/styled';

import useOutsideClick from '../../hooks/ui-control/useOutsideClick';
import ChevronFormDown from '../Icons/ChevronFormDown';
import ChevronFormUp from '../Icons/ChevronFormUp';
import { FieldErrorInfo, InputLabel, InputWrapper } from './FormField';

interface CustomSelectFieldWithCustomItemCardProps {
  label?: any;
  id: string;
  name: string;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
  renderContent: { label: string; value: string; [key: string]: any }[];
  renderComponent: (data: any) => React.ReactNode;
  showErrorLabel?: boolean;
}

export const CustomSelectFieldWithCustomItemCard: React.FC<
  CustomSelectFieldWithCustomItemCardProps
> = ({
  label,
  className,
  renderContent,
  renderComponent,
  onChange,
  showErrorLabel = true,
  ...props
}) => {
  const selectFieldRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  useOutsideClick(selectFieldRef, () => setIsOpen(false));

  const [field, meta, helper] = useField(props);

  const selectedItem = renderContent.find((item) => item.value === field.value);

  const handleOptionClick = (item: { label: string; value: string }) => {
    helper.setValue(item.value);
    setIsOpen(false);
    if (onChange) onChange(item.value);
  };

  return (
    <InputWrapper className={className}>
      {label && (
        <InputLabel as='div' htmlFor={props.name || props.id}>
          {label}
        </InputLabel>
      )}
      <InputContainer ref={selectFieldRef}>
        <TriggerBox
          onClick={() => setIsOpen((prev) => !prev)}
          className={isOpen ? 'active' : ''}
        >
          <span>{selectedItem?.label ?? props.placeholder ?? ''}</span>
          {isOpen ? <ChevronFormUp /> : <ChevronFormDown />}
        </TriggerBox>

        {isOpen && (
          <SelectOptionBox>
            {renderContent.length !== 0 ? (
              renderContent.map((data, index) => (
                <Option
                  key={index}
                  onClick={() => handleOptionClick(data)}
                  className={data.value === field.value ? 'isSelected' : ''}
                >
                  {renderComponent(data)}
                </Option>
              ))
            ) : (
              <EmptyState>
                <em>No options available</em>
              </EmptyState>
            )}
          </SelectOptionBox>
        )}
      </InputContainer>
      {showErrorLabel && meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default CustomSelectFieldWithCustomItemCard;

const TriggerBox = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 400;
  font-size: 14px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;

  &.active {
    border-color: ${({ theme }) => theme.palette.logiratePrimary};
  }

  &:hover {
    border-color: ${({ theme }) => theme.palette.logiratePrimary};
  }
`;

export const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const SelectOptionBox = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
  max-height: 180px;
  display: flex;
  overflow: auto;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  box-shadow: 1px 4px 12px -1px rgba(44, 78, 39, 0.15);
  border-radius: 4px;
  background-color: white;
  margin-top: 4px;
  z-index: 10;
  color: ${({ theme }) => theme.palette.grayTextColor};

  /* border: 1px solid red; */
`;

export const Option = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  font-size: 14px;
  /* border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor}; */

  &:hover {
    background-color: #f9fafb;
  }

  &:last-of-type {
    border: none;
  }

  &.isSelected {
    background-color: #f9fafb;
  }
`;

const EmptyState = styled.div`
  display: flex;
  padding: 16px;
  cursor: not-allowed;
  font-weight: 500;
  font-size: 14px;
  color: gray;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
